<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import VueRecaptcha from 'vue3-recaptcha2'
import createEmailAlert from '../graphql/createEmailAlert.gql'

const config = useRuntimeConfig()
const localePath = useLocalePath()
const { locale, t } = useI18n()

defineI18nRoute({
  paths: {
    'en-CA': '/investor-relations/email-alerts',
    'fr-CA': '/relations-avec-investisseurs/alertes-courriel'
  }
})

useHead({
  title: t('navigation.emailAlerts.metatitle'),
  meta: [{ name: 'description', content: t('navigation.emailAlerts.metadescription') }]
})

const router = useRouter()

const firstname = ref('')
const lastname = ref('')
const email = ref('')
const captchaResponse = ref()
const isSubmitting = ref(false)

const recaptchaVerified = (response: string) => {
  captchaResponse.value = response
}
const recaptchaExpired = () => {
  captchaResponse.value = null
}
const recaptchaFailed = () => {}

const submitForm = async () => {
  try {
    if (!captchaResponse.value || firstname.value === '' || lastname.value === '' || email.value === '') {
      if (locale.value === 'en-CA') {
        alert('Please fill in all the required fields')
      } else {
        alert('Veuillez compléter tous les champs obligatoires')
      }

      isSubmitting.value = false
      return
    } else {
      // Send the recaptchaResponse token to your server for verification
      const response = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: captchaResponse.value })
      })

      const result = await response.json()

      if (result.success) {
        const { mutate } = await useMutation(createEmailAlert, {
          variables: {
            firstName: firstname.value,
            lastName: lastname.value,
            email: email.value
          }
        })
        const _response = await mutate()

        if (locale.value === 'en-CA') {
          router.push('/site-subscription-confirmation-page')
        } else {
          router.push('/fr-CA/page-confirmation-abonnement-sur-site')
        }
      }
    }
  } catch (error) {
    console.error('Error: ', error)
  }

  isSubmitting.value = false
}
</script>

<template>
  <div class="wrapContent">
    <div class="entetePage">
      <div class="wrapper">
        <div class="filAriane">
          <span class="">
            <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <NuxtLink :to="localePath('investorRelations-investorRelations')">{{ $t('navigation.investorRelations.name') }}</NuxtLink>
            <span class=""> &gt; </span>
            <span class="">{{ $t('navigation.emailAlerts.name') }}</span>
          </span>
        </div>
        <div class="title">
          <h1><span class="">{{ $t('navigation.emailAlerts.name') }}</span></h1>
        </div>
      </div>
    </div>
    <div class="contentPage">
      <div class="wrapper clearfix">
        <MenuInvestorRelations />
        <div class="contenu right">
          <div v-if="$i18n.locale == 'en-CA'"
               class="">
            <p>You may automatically receive KP Tissue Inc. financial information by email.</p>
            <form>
              <label for="firstname">First name *</label><br>
              <input id="firstname"
                     v-model="firstname"
                     type="text"
                     name="firstname"
                     required><br>
              <label for="lastname">Last name *</label><br>
              <input id="lastname"
                     v-model="lastname"
                     type="text"
                     name="lastname"
                     required><br>
              <label for="email">Email *</label><br>
              <input id="email"
                     v-model="email"
                     type="email"
                     name="email"
                     required><br><br>
              <VueRecaptcha ref="vueRecaptcha"
                :sitekey="config.public.recaptchaKey"
                size="normal"
                theme="light"
                @verify="recaptchaVerified"
                @expire="recaptchaExpired"
                @fail="recaptchaFailed"
              />
              <br>
              <input type="submit"
                     value="Send"
                     class="form-submit"
                     :disabled="isSubmitting"
                     @click.prevent="() => submitForm()">
            </form>
          </div>
          <div v-if="$i18n.locale == 'fr-CA'"
               class="">
            <p>Vous pouvez recevoir automatiquement par courriel l’information financière de Papiers Tissu KP inc.</p>
            <form>
              <label for="firstname">Prénom *</label><br>
              <input id="firstname"
                     v-model="firstname"
                     type="text"
                     name="firstname"
                     required><br>
              <label for="lastname">Nom de famille *</label><br>
              <input id="lastname"
                     v-model="lastname"
                     type="text"
                     name="lastname"
                     required><br>
              <label for="email">Courriel *</label><br>
              <input id="email"
                     v-model="email"
                     type="email"
                     name="email"
                     required><br><br>
              <VueRecaptcha ref="vueRecaptcha"
                :sitekey="config.public.recaptchaKey"
                size="normal"
                theme="light"
                @verify="recaptchaVerified"
                @expire="recaptchaExpired"
                @fail="recaptchaFailed"
              />
              <br>
              <input type="submit"
                     value="Soumettre"
                     class="form-submit"
                     :disabled="isSubmitting"
                     @click.prevent="() => submitForm()">
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
